import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'

import { Dropdown, type DropdownProps, type DropdownChangeParams } from 'primereact/dropdown'

import './InlineSelector.scss'

const CLASS = 'insights-portal-inline-selector'

type InlineSelectorSelectItem<K extends string, V extends string, VT> = {
  [key in K]: string // K defines the field name for the key
} & {
  [value in V]: VT // V defines the field name for the value
}

type InlineSelectorProps = Omit<DropdownProps, 'value' | 'options'> & {
  className: string
  label: string
  onChange: (event: { value: unknown }) => void
  value?: string
  labelAsValue?: boolean
  options: InlineSelectorSelectItem<'code', 'name', string>[]
}

type ChildElement = InlineSelectorSelectItem<'code', 'name', string>
type ParentElement = ChildElement & {
  items?: ChildElement[]
}

const InlineSelector: React.FC<InlineSelectorProps> = props => {
  const {
    labelAsValue = true,
    options: propsOptions,
    value: propsValue,
    label: propsLabel,
    onChange: propsOnChange,
    confirmKeyboardSelect,
    className,
    optionLabel = 'name',
    optionValue = 'code',
    filterPlaceholder,
    optionGroupChildren,
    ...rest
  } = props

  const options = useMemo(() => propsOptions || [], [propsOptions])

  const inputRef = useRef<HTMLSelectElement>(null)

  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>(null)

  const value = useMemo<unknown>(
    () =>
      (labelAsValue
        ? (options as ParentElement[])
            .flatMap(
              item =>
                (optionGroupChildren in item
                  ? (item[optionGroupChildren as 'items'] as unknown)
                  : [item]) as ChildElement[]
            )
            .find(o => o[optionLabel as 'name'] === propsValue)?.[optionValue as 'code']
        : propsValue) || null,
    [labelAsValue, options, optionGroupChildren, optionValue, propsValue, optionLabel]
  )

  const currentLabel = useMemo(
    () =>
      labelAsValue
        ? propsValue
        : (options as ParentElement[])
            .flatMap(
              item =>
                (optionGroupChildren in item
                  ? (item[optionGroupChildren as 'items'] as unknown)
                  : [item]) as ChildElement[]
            )
            .find(o => o[optionValue as 'code'] === propsValue)?.[optionLabel as 'name'],
    [labelAsValue, propsValue, options, optionGroupChildren, optionLabel, optionValue]
  )

  const valueTemplateRef = useRef<(arg?: unknown) => React.ReactNode>(null)
  valueTemplateRef.current = () =>
    propsLabel ? (
      <div className={`${CLASS}-text`}>
        <div className={`${CLASS}-label`}>{propsLabel}</div>
        <div className={`${CLASS}-value`} title={propsValue}>
          {currentLabel}
        </div>
      </div>
    ) : (
      <div className={`${CLASS}-value`}>{currentLabel}</div>
    )

  const onChangeRef = useRef<(event: { value: unknown }) => void>(null)
  onChangeRef.current = propsOnChange
  const onChange = useCallback((event: DropdownChangeParams) => {
    if (onChangeRef.current) {
      onChangeRef.current({ value: event.value })
    }
  }, [])

  useEffect(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      setPanelStyle({ width: `${rect.width}px` })
    }
  }, [])

  return (
    <Dropdown
      appendTo={document.body}
      confirmKeyboardSelect={confirmKeyboardSelect || false}
      inputRef={inputRef}
      className={`insights-portal ${CLASS}${className ? ` ${className}` : ''}`}
      panelClassName="insights-portal PRAYTMTheme"
      panelStyle={panelStyle}
      value={value}
      options={options}
      optionLabel={optionLabel}
      optionValue={optionValue}
      optionGroupChildren={optionGroupChildren}
      valueTemplate={valueTemplateRef.current}
      filter={options.length > 10}
      filterPlaceholder={filterPlaceholder || 'Search items'}
      resetFilterOnHide={true}
      onChange={onChange}
      {...rest}
    />
  )
}

export default InlineSelector
